import { motion } from 'framer-motion'
import { Model } from '@/types/api'
import { formatFileSize } from '@/utils/helpers'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'

export interface ModelCardProps {
  model: Model
  selected: boolean
  onSelect: (modelName: string) => void
}

const ModelCard = ({ model, selected, onSelect }: ModelCardProps) => {
  const getBadgeForModel = (model: Model) => {
    const badges = []

    if (model.name === 'dwsep_se_a075') {
      badges.push({ icon: '👑', label: 'Best Overall', variant: 'success' as const })
    }

    // Size badge (always show an icon for each model)
    if (model.size_bytes <= 120000) {
      badges.push({ icon: '🪶', label: 'Smallest', variant: 'warning' as const })
    } else if (model.size_bytes <= 160000) { 
      badges.push({ icon: '📦', label: 'Compact', variant: 'info' as const })
    } else if (model.size_bytes > 200000) {
      badges.push({ icon: '🧱', label: 'Full-size', variant: 'info' as const })
    }

    // Accuracy badge
    if (model.accuracy && model.accuracy >= 1.0) {
      badges.push({ icon: '🏆', label: 'Most Accurate', variant: 'success' as const })
    }

    // Speed badge
    if (model.latency_ms && model.latency_ms < 8) {
      badges.push({ icon: '⚡', label: 'Fastest', variant: 'info' as const })
    }

    // Default badge
    if (model.default) {
      badges.push({ icon: '⭐', label: 'Default', variant: 'info' as const })
    }

    return badges
  }

  const badges = getBadgeForModel(model)

  return (
    <Card
      hover
      selected={selected}
      onClick={() => onSelect(model.name)}
      className="cursor-pointer relative overflow-hidden"
    >
      {/* Selection indicator */}
      {selected && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          className="absolute top-3 right-3 w-6 h-6 bg-primary-500 rounded-full flex items-center justify-center"
        >
          <svg
            className="w-4 h-4 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </motion.div>
      )}

      {/* Model name */}
      <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-50 mb-2 pr-8">
        {model.display_name || model.name}
      </h3>

      {/* Description */}
      {model.description && (
        <p className="text-sm text-gray-600 dark:text-dark-400 mb-3 line-clamp-2">
          {model.description}
        </p>
      )}

      {/* Badges */}
      {badges.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {badges.map((badge, index) => (
            <Badge key={index} variant={badge.variant}>
              <span className="mr-1">{badge.icon}</span>
              {badge.label}
            </Badge>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 gap-3 pt-3 border-t border-gray-200 dark:border-dark-700">
        {model.accuracy !== undefined && (
          <div>
            <p className="text-xs text-gray-500 dark:text-dark-500 uppercase tracking-wider mb-1">
              Accuracy
            </p>
            <p className="text-sm font-semibold text-gray-800 dark:text-dark-200">
              {(model.accuracy * 100).toFixed(1)}%
            </p>
          </div>
        )}

        {model.latency_ms !== undefined && (
          <div>
            <p className="text-xs text-gray-500 dark:text-dark-500 uppercase tracking-wider mb-1">
              Latency
            </p>
            <p className="text-sm font-semibold text-gray-800 dark:text-dark-200">
              {model.latency_ms.toFixed(1)}ms
            </p>
          </div>
        )}

        <div>
          <p className="text-xs text-gray-500 dark:text-dark-500 uppercase tracking-wider mb-1">
            Size
          </p>
          <p className="text-sm font-semibold text-gray-800 dark:text-dark-200">
            {formatFileSize(model.size_bytes)}
          </p>
        </div>
      </div>
    </Card>
  )
}

export default ModelCard
